"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Clock, Loader2 } from "lucide-react"
import { apiClient } from "../lib/utils"

interface PendingTimesheet {
  timesheetDuration: string
}

interface PendingTimesheetModalProps {
  isOpen: boolean
  onClose: () => void
}

export function PendingTimesheetModal({ isOpen, onClose }: PendingTimesheetModalProps) {
  const [pendingTimesheets, setPendingTimesheets] = useState<PendingTimesheet[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchPendingTimesheets()
    } else {
      // Reset state when modal closes
      setPendingTimesheets([])
      setError(null)
    }
  }, [isOpen])

  const fetchPendingTimesheets = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get("/Dashboard/getemployeedefaults")
      if (Array.isArray(response)) {
        setPendingTimesheets(response)
      } else if (response?.data && Array.isArray(response.data)) {
        setPendingTimesheets(response.data)
      } else {
        setPendingTimesheets([])
      }
    } catch (err: any) {
      console.error("Error fetching pending timesheets:", err)
      setError("Failed to load pending timesheets. Please try again.")
      setPendingTimesheets([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-[80vh] p-0">
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Pending Timesheets
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading data...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          ) : pendingTimesheets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-sm">No pending timesheets found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <ul className="space-y-3">
                {pendingTimesheets.map((ts, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-orange-500 mt-0.5 flex-shrink-0">•</span>
                    <span className="leading-relaxed text-gray-700">{ts.timesheetDuration}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end pt-4 pb-6 px-6 border-t border-gray-200 flex-shrink-0">
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold">{pendingTimesheets.length}</span> pending timesheet{pendingTimesheets.length !== 1 ? "s" : ""}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

